// ObligationListPage — framework list for the selected category.
// Rendered in the right panel of ObligationLayout.
// Route: /t/:slug/obligations (index route)

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedObligationCategory } from './ObligationLayout'
import {
  obligationsApi,
  type ComplianceFramework,
} from '@/lib/api/obligations'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Input } from '@/components/ui/input'
// Note: no Progress component — using inline bar
import {
  Search,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react'

// --- Sort types ---

type SortColumn = 'name' | 'category' | 'items' | 'coverage'
type SortDirection = 'asc' | 'desc'

interface SortState {
  column: SortColumn | null
  direction: SortDirection
}

function sortFrameworks(frameworks: ComplianceFramework[], sort: SortState): ComplianceFramework[] {
  if (!sort.column) return frameworks
  const sorted = [...frameworks].sort((a, b) => {
    let cmp = 0
    switch (sort.column) {
      case 'name':
        cmp = a.name.localeCompare(b.name)
        break
      case 'category':
        cmp = a.category_name.localeCompare(b.category_name)
        break
      case 'items':
        cmp = a.item_count - b.item_count
        break
      case 'coverage': {
        const aCov = a.item_count > 0 ? a.mapped_count / a.item_count : 0
        const bCov = b.item_count > 0 ? b.mapped_count / b.item_count : 0
        cmp = aCov - bCov
        break
      }
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
  return sorted
}

export function ObligationListPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const { categoryId, categoryName } = useSelectedObligationCategory()

  function handleFrameworkClick(frameworkId: string) {
    navigate({ to: `/t/${slug}/obligations/${frameworkId}` })
  }

  if (!categoryId) {
    return <EmptySelection />
  }

  return (
    <FrameworkListPanel
      categoryId={categoryId}
      categoryName={categoryName}
      onFrameworkClick={handleFrameworkClick}
      slug={slug}
    />
  )
}

// --- Empty state when no category is selected ---

function EmptySelection() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <div className="space-y-2 text-center">
        <p className="text-sm">Select a framework category to view obligations</p>
      </div>
    </div>
  )
}

// --- Framework list panel ---

interface FrameworkListPanelProps {
  categoryId: string
  categoryName: string
  onFrameworkClick: (frameworkId: string) => void
  slug: string
}

function FrameworkListPanel({
  categoryId,
  categoryName,
  onFrameworkClick,
  slug,
}: FrameworkListPanelProps) {
  const api = useApi()
  const oblApi = obligationsApi(api)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>({ column: null, direction: 'asc' })

  const {
    data: frameworks,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.complianceFrameworks.list(slug, {
      category_id: categoryId,
      search: search || undefined,
    }),
    queryFn: () => oblApi.list({ category_id: categoryId, search: search || undefined }),
  })

  const sorted = useMemo(
    () => (frameworks ? sortFrameworks(frameworks, sort) : []),
    [frameworks, sort],
  )

  const handleSort = useCallback(
    (column: SortColumn) => {
      setSort((prev) => ({
        column,
        direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
      }))
    },
    [],
  )

  function SortIcon({ column }: { column: SortColumn }) {
    if (sort.column !== column) return null
    return sort.direction === 'asc' ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h2 className="text-lg font-semibold">{categoryName}</h2>
          <p className="text-sm text-muted-foreground">
            {sorted.length} framework{sorted.length !== 1 ? 's' : ''}
          </p>
        </div>

      </div>

      {/* Search */}
      <div className="border-b px-4 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search frameworks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-8"
          />
          {search && (
            <button
              className="absolute right-2.5 top-2.5"
              onClick={() => setSearch('')}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <TableSkeleton rows={5} />
        ) : error ? (
          <div className="p-4">
            <ErrorState
              message="Failed to load frameworks"
              technicalDetail={error.message}
              onRetry={() => refetch()}
            />
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <p className="text-sm">
              {search
                ? 'No frameworks match your search'
                : 'No frameworks in this category. Import one to get started.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th
                  className="cursor-pointer px-4 py-2"
                  onClick={() => handleSort('name')}
                >
                  Name <SortIcon column="name" />
                </th>
                <th
                  className="cursor-pointer px-4 py-2 text-right"
                  onClick={() => handleSort('items')}
                >
                  Requirements <SortIcon column="items" />
                </th>
                <th
                  className="cursor-pointer px-4 py-2"
                  onClick={() => handleSort('coverage')}
                >
                  Coverage <SortIcon column="coverage" />
                </th>
                <th className="w-8 px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((fw) => (
                <FrameworkRow
                  key={fw.id}
                  framework={fw}
                  onClick={() => onFrameworkClick(fw.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// --- Framework row ---

function FrameworkRow({
  framework,
  onClick,
}: {
  framework: ComplianceFramework
  onClick: () => void
}) {
  const coveragePercent =
    framework.item_count > 0
      ? Math.round((framework.mapped_count / framework.item_count) * 100)
      : 0

  return (
    <tr
      className="cursor-pointer border-b transition-colors hover:bg-accent/50"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div className="font-medium">{framework.name}</div>
        {framework.imported_from && (
          <div className="text-xs text-muted-foreground">
            Imported from {framework.imported_from.startsWith('library:') ? 'library' : 'CSV'}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        <span className="text-sm">{framework.item_count}</span>
        <span className="ml-1 text-xs text-muted-foreground">
          ({framework.mapped_count} mapped)
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${coveragePercent}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">
            {coveragePercent}%
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </td>
    </tr>
  )
}
